import React, { useCallback, useState } from 'react'
import { useStripe } from '@stripe/react-stripe-js'
import { Activity, ConfirmModal } from '@Knowledge-OTP/znk-ui-components'
import { useDispatch } from 'react-redux'
import { gql, useMutation } from '@apollo/client'
import useTranslation from '../../../../../../../i18n/useTranslation'
import { showAlert } from '../../../../../../../common/Alert/util'
import useCreateBankAccountSetupIntent from './useCreateBankAccountSetupIntent'

/**
 * Attaches a Stripe payment method (e.g. us_bank_account from Financial Connections) to the user.
 * Backend may use the same mutation as cards or a dedicated one for bank accounts.
 */
const SET_BANK_PAYMENT_METHOD = gql`
  mutation saveBankAccount($paymentMethodId: String!, $userId: String!) {
    saveBankAccount(paymentMethodId: $paymentMethodId, userId: $userId) {
      last4
      bankName
      validationStatus
    }
  }
`

const CANCEL_SETUP_INTENT = gql`
  mutation cancelSetupIntentBankAccount($setupIntentId: String!) {
    cancelSetupIntentBankAccount(setupIntentId: $setupIntentId)
  }
`

const AddBankDebitFc = ({ userId, refetch, customerEmail, customerName }) => {
  const stripe = useStripe()
  const dispatch = useDispatch()
  const { translate } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [verificationModalOpen, setVerificationModalOpen] = useState(false)
  const [verificationUrl, setVerificationUrl] = useState('')
  const [createSetupIntent, { loading: loadingSetupIntent }] = useCreateBankAccountSetupIntent()
  const [saveBankAccount, { loading: attaching }] = useMutation(SET_BANK_PAYMENT_METHOD)
  const [cancelSetupIntent, {loading: cancelSt}] = useMutation(CANCEL_SETUP_INTENT)

  const handleAttachPaymentMethod = useCallback(
    (paymentMethodId) => {
      saveBankAccount({
        variables: { paymentMethodId, userId },
      })
        .then(({ data }) => {
            console.log('data')
          const result = data?.saveBankAccount
          if (result) {
            showAlert(
              { text: translate('profile.actions.addBankDebit.successAlert'), status: 'success' },
              dispatch
            )
            refetch()
          } else {
            showAlert(
              {
                text: result?.error || translate('profile.actions.addBankDebit.errorAlert'),
                status: 'error',
              },
              dispatch
            )
          }
        })
        .catch((err) => {
          showAlert(
            { text: err.message, status: 'error' },
            dispatch
          )
          console.error(err)
        })
        .finally(() => setLoading(false))
    },
    [userId, refetch, saveBankAccount, translate, dispatch]
  )

  const runCollectBankAccount = useCallback(
    async (clientSecret) => {
      if (!stripe || !clientSecret) {
        setLoading(false)
        return
      }
      console.log(clientSecret)
      try {
        const result = await stripe.collectBankAccountForSetup({
          clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {name: customerName, email: customerEmail},
            },
          },
        });

        if (result?.error) {
          if (result.error.code !== 'cancelled_by_user') {
            showAlert(
              {
                text: result.error.message || translate('profile.actions.addBankDebit.errorAlert'),
                status: 'error',
              },
              dispatch
            )
          }
          setLoading(false)
          return
        }
        // user canceled the operation
        if (result?.setupIntent?.status === 'requires_payment_method') {
          cancelSetupIntent({
            variables: {setupIntentId: result?.setupIntent?.id}
          }).then(() => {
            setLoading(cancelSt) 
          }).catch((error) => {
            showAlert(
              {
                text: error || translate('profile.actions.addBankDebit.errorAlert'),
                status: 'error',
              },
              dispatch
            )
            setLoading(false)
          })
          .finally(() => setLoading(cancelSt))
          return
        }
        let confirmResult = {}
        if (result.setupIntent.status === 'requires_confirmation') {
          console.log(result.setupIntent.status)
          console.log(result?.setupIntent?.id)
          confirmResult = await stripe.confirmUsBankAccountSetup(result?.setupIntent?.client_secret);
          console.log(confirmResult)
          if (confirmResult.error) {
            showAlert(
              { text: confirmResult.error.message || translate('profile.actions.addBankDebit.errorAlert'), status: 'error' },
              dispatch
            )
            setLoading(false)
            // if (onCancel) onCancel()
            return
          }
          const hostedUrl = confirmResult?.setupIntent?.next_action?.verify_with_microdeposits?.hosted_verification_url
          if (confirmResult?.setupIntent?.status === 'requires_action' && hostedUrl) {
            console.log('im here!!')
            setVerificationUrl(hostedUrl)
            setVerificationModalOpen(true)
            setLoading(false)
            // if (onCancel) onCancel()
            return
          }
          console.log(confirmResult)
  
          console.log('✅ Confirmado:', confirmResult.setupIntent.status);
        }
        const paymentMethodId = confirmResult?.setupIntent?.payment_method
        console.log(paymentMethodId)
        if (paymentMethodId && confirmResult?.setupIntent?.status === 'succeeded') {
          handleAttachPaymentMethod(paymentMethodId,userId)
        }
        // user cancel operation
        // if (confirmResult?.setupIntent?.status === 'requires_payment_method') return
      } catch (err) {
        console.log(err)
        setLoading(false)
        showAlert(
          { text: translate('profile.actions.addBankDebit.errorAlert'), status: 'error' },
          dispatch
        )
        console.error('error to cancel ' + err)
      }
    },
    // eslint-disable-next-line
    [stripe, handleAttachPaymentMethod, translate, dispatch, customerEmail, customerName]
  )

  const handleClick = useCallback(() => {
    if (!stripe || !userId || loading || loadingSetupIntent || attaching) return

    setLoading(true)
    createSetupIntent({
      variables: { userId },
    })
      .then(({ data }) => {
        console.log(data)
        const clientSecret = data?.createBankAccountSetupIntent
        if (clientSecret) {
          return runCollectBankAccount(clientSecret)
        }
        setLoading(false)
        showAlert(
          { text: translate('profile.actions.addBankDebit.errorAlert'), status: 'error' },
          dispatch
        )
      })
      .catch((err) => {
        setLoading(false)
        showAlert(
          { text: translate('profile.actions.addBankDebit.errorAlert'), status: 'error' },
          dispatch
        )
        console.error(err)
      })
  }, [
    stripe,
    userId,
    loading,
    loadingSetupIntent,
    attaching,
    createSetupIntent,
    runCollectBankAccount,
    translate,
    dispatch,
  ])

  const isLoading = loading || loadingSetupIntent || attaching
  const toggleVerificationModal = () => setVerificationModalOpen((v) => !v)

  return (
    <>
      <span
        className="btn-link m-0 p-0"
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        role="button"
        tabIndex={0}
      >
        {isLoading ? (
          <Activity style={{ marginTop: '7px' }} />
        ) : (
          translate('profile.actions.changeMethod.addBankDebit')
        )}
      </span>
      <ConfirmModal
        className="m-0 p-0 mt-5 text-break small d-flex justify-content-center"
        isOpen={verificationModalOpen}
        toggle={toggleVerificationModal}
        backdrop="static"
        title={translate('profile.actions.addBankDebit.verificationRequired') || 'Verification required'}
        body={
          (translate('profile.actions.addBankDebit.verificationUrlMessage') || 'Complete verification in your bank\'s page:')
        }
        confirmAction={() => window.open(verificationUrl, '_blank', 'noopener,noreferrer')}
        cancelAction={toggleVerificationModal}
        confirmText={translate('profile.actions.addBankDebit.openVerificationUrl') || 'Open verification link'}
        cancelText={translate('misc.close') || 'Close'}
      />
    </>
  )
}

export default AddBankDebitFc
